import { Manga, KnownSite, TierGrade, TIER_ORDER } from '../types/manga';
import { INITIAL_MANGA_LIST } from '../data/initialData';

const STORAGE_MANGA_KEY = 'manga_tier_tracker_data_v3';
const STORAGE_SITES_KEY = 'manga_tier_tracker_sites_v2';

export const DEFAULT_KNOWN_SITES: KnownSite[] = [
  { name: '1668manga.com', url: 'https://1668manga.com' },
  { name: 'up-manga.com', url: 'https://www.up-manga.com' },
  { name: 'mangablackcat.com', url: 'https://mangablackcat.com' },
];

/**
 * Get manga data:
 * 1. Checks LocalStorage (v3)
 * 2. If empty, loads the pristine bundled INITIAL_MANGA_LIST (70 manga)
 */
export function getLocalManga(): Manga[] {
  try {
    // Clear corrupted legacy v2 cache if present
    localStorage.removeItem('manga_tier_tracker_data_v2');

    const raw = localStorage.getItem(STORAGE_MANGA_KEY);
    if (raw) {
      const data = JSON.parse(raw);
      if (Array.isArray(data) && data.length > 0) {
        return sanitizeMangaList(data);
      }
    }
  } catch (err) {
    console.error('Error reading manga from localStorage:', err);
  }

  // Initial Seed
  return sanitizeMangaList([...INITIAL_MANGA_LIST]);
}

const KNOWN_DEAD_UPMANGA_SLUGS = [
  'up-manga.com/a-barbarian-from-the-middle-ages',
  'up-manga.com/manga/a-barbarian-from-the-middle-ages',
  'up-manga.com/regressor-of-the-fallen-family',
  'up-manga.com/the-end-has-come',
  'up-manga.com/got-dropped-into-a-ghost-story-still-gotta-work',
  'up-manga.com/a-secretly-capable-child-is-seeking-for-her-dad',
  'up-manga.com/absolute-regression',
  'up-manga.com/pay-to-win-martial-god',
  'up-manga.com/my-father-is-the-number-one-under-heaven',
  'up-manga.com/the-strongest-sword-saint-reincarnated-into-a-prestigious-magic-family',
  'up-manga.com/the-crazy-villain-regains-his-sanity',
  'up-manga.com/to-hell with-being-heir-im-going-to-heal',
  'up-manga.com/to-hell-with-being-heir-im-going-to-heal',
  'up-manga.com/my-wife-waited-in-the-wheat-fields',
  'up-manga.com/the-martial-genius-who-remembers-everything',
];

function isDeadSourceUrl(url?: string): boolean {
  if (!url) return true;
  const lower = url.toLowerCase();
  return KNOWN_DEAD_UPMANGA_SLUGS.some((pattern) => lower.includes(pattern));
}

export function sanitizeMangaList(data: any[]): Manga[] {
  if (!Array.isArray(data)) return [];
  const sanitized = data.map((m: any) => {
    const cleanTitle = (m.title || '')
      .replace(/[\ufffd\uFFFD]/g, "'")
      .replace(/’/g, "'")
      .replace(/'\s*'/g, "'")
      .replace(/\s+/g, ' ')
      .trim();

    let cleanStatus = m.status;
    if (!cleanStatus || cleanStatus.includes('\ufffd') || cleanStatus.startsWith('??')) {
      cleanStatus = 'ยังไม่จบ';
    }

    let cleanNationality = m.nationality;
    if (!cleanNationality || cleanNationality.includes('\ufffd') || cleanNationality.includes('?')) {
      cleanNationality = '🇰🇷';
    }

    let sources = Array.isArray(m.sources) ? m.sources.map((s: any) => (s ? { ...s } : s)) : [];

    // Purge known corrupted / mismatched / dead 404 URLs from sources
    sources = sources.filter((s: any) => {
      if (!s || !s.url) return false;
      if (isDeadSourceUrl(s.url)) return false;
      return true;
    });

    // Ensure all 1668manga URLs have canonical /manga/ prefix
    sources = sources.map((s: any) => {
      if (s && s.url && s.url.includes('1668manga.com') && !s.url.includes('1668manga.com/manga/')) {
        return { ...s, url: s.url.replace('1668manga.com/', '1668manga.com/manga/') };
      }
      return s;
    });

    let currentCh = Number(m.currentCh) || 0;
    let latestSourceUrl = isDeadSourceUrl(m.latestSourceUrl) ? '' : m.latestSourceUrl;
    let latestSourceName = isDeadSourceUrl(m.latestSourceUrl) ? '' : m.latestSourceName;

    // If this is barbarian and was corrupted by up-manga or lost its source
    if (m.title?.toLowerCase().includes('barbarian from the middle ages')) {
      const has1668 = sources.some((s: any) => s.url.includes('1668manga.com'));
      if (!has1668) {
        sources.unshift({
          name: '1668manga.com',
          url: 'https://1668manga.com/manga/a-barbarian-from-the-middle-ages/',
          chapter: 32,
        });
      } else {
        const s1668 = sources.find((s: any) => s.url.includes('1668manga.com'));
        if (s1668 && (!s1668.chapter || s1668.chapter === 0)) {
          s1668.chapter = 32;
        }
      }
      if (currentCh === 48 || currentCh === 0 || latestSourceUrl?.toLowerCase().includes('up-manga.com')) {
        currentCh = 32;
        latestSourceName = '1668manga.com';
        latestSourceUrl = 'https://1668manga.com/manga/a-barbarian-from-the-middle-ages/';
      }
    }

    // If this is Martial God and needs valid source repair
    if (m.title?.toLowerCase().includes('the martial god who levels up')) {
      const has1668 = sources.some((s: any) => s.url.includes('1668manga.com'));
      if (!has1668) {
        sources.unshift({
          name: '1668manga.com',
          url: 'https://1668manga.com/manga/the-martial-god-who-levels-up/',
          chapter: 48,
        });
      } else {
        const s1668 = sources.find((s: any) => s.url.includes('1668manga.com'));
        if (s1668 && (!s1668.chapter || s1668.chapter === 0)) {
          s1668.chapter = 48;
        }
      }
      if (currentCh === 0) {
        currentCh = 48;
        latestSourceName = '1668manga.com';
        latestSourceUrl = 'https://1668manga.com/manga/the-martial-god-who-levels-up/';
      }
    }

    // If this is Nano Machine, ensure mangablackcat has chapter 331+
    if (m.title?.toLowerCase().includes('nano machine')) {
      const sMb = sources.find((s: any) => s.url?.includes('mangablackcat.com'));
      if (sMb && (!sMb.chapter || sMb.chapter < 331)) {
        sMb.chapter = 331;
      }
      if (currentCh < 331) {
        currentCh = 331;
      }
    }

    // If this is Third-Rate Martial Family, ensure valid sources
    if (m.title?.toLowerCase().includes('third-rate martial family')) {
      const s1668 = sources.find((s: any) => s.url?.includes('1668manga.com'));
      if (s1668) {
        s1668.chapter = s1668.chapter || 27;
        s1668.url = 'https://1668manga.com/manga/third-rate-martial-family-becomes-the-best-under-heaven/';
      } else {
        sources.push({
          name: '1668manga.com',
          url: 'https://1668manga.com/manga/third-rate-martial-family-becomes-the-best-under-heaven/',
          chapter: 27,
        });
      }
      if (currentCh < 27) currentCh = 27;
    }

    // If this is Who Let Him to Cultivate Immortality, ensure not 0
    if (m.title?.toLowerCase().includes('who let him to cultivate immortality')) {
      const s1668 = sources.find((s: any) => s.url?.includes('1668manga.com'));
      if (s1668 && (!s1668.chapter || s1668.chapter === 0)) {
        s1668.chapter = 29;
      }
      if (currentCh === 0) currentCh = 29;
    }

    // Keep currentCh strictly non-decreasing and in sync with valid sources
    if (sources.length > 0) {
      let maxSrcCh = 0;
      let bestSource = sources[0];
      for (const s of sources) {
        const ch = Number(s.chapter) || 0;
        if (ch > maxSrcCh) {
          maxSrcCh = ch;
          bestSource = s;
        }
      }
      if (maxSrcCh > currentCh) {
        currentCh = maxSrcCh;
        latestSourceUrl = bestSource.url;
        latestSourceName = bestSource.name;
      } else if (currentCh > maxSrcCh && maxSrcCh > 0) {
        // If currentCh was manually set or higher, keep top source chapter in sync
        if (sources[0]) {
          sources[0] = { ...sources[0], chapter: currentCh };
        }
      }
      // Ensure latestSourceUrl still exists in valid sources
      const validLatest = sources.find((s: any) => s.url === latestSourceUrl);
      if (!validLatest && bestSource) {
        latestSourceUrl = bestSource.url;
        latestSourceName = bestSource.name;
      }
    }

    return {
      ...m,
      imageUrl: m.imageUrl || '',
      title: cleanTitle,
      status: cleanStatus,
      nationality: cleanNationality,
      sources,
      currentCh,
      latestSourceUrl: latestSourceUrl || sources[0]?.url || '',
      latestSourceName: latestSourceName || sources[0]?.name || '',
      grade: m.grade,
    };
  });

  // Specific fix: if "Murim's Youngest Miracle Demon Doctor" was placed at the front of EX tier,
  // move it to the very end of EX tier (after "The World's Best Engineer") as requested by user.
  const demonDoctor = sanitized.find((m) =>
    m.title?.toLowerCase().includes('youngest miracle demon doctor')
  );
  let processed = sanitized;
  if (demonDoctor && demonDoctor.grade === 'EX') {
    const exItems = sanitized
      .filter((m) => m.grade === 'EX')
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    if (exItems.length > 1 && exItems[0].id === demonDoctor.id) {
      const withoutDoctor = sanitized.filter((m) => m.id !== demonDoctor.id);
      const exWithout = withoutDoctor
        .filter((m) => m.grade === 'EX')
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      const lastEx = exWithout[exWithout.length - 1];
      const maxExOrder = (lastEx?.order ?? 9999) + 1;
      demonDoctor.order = maxExOrder;
      processed = [...withoutDoctor, demonDoctor];
    }
  }

  // Canonicalize tier ordering: S -> A+ -> A -> B+ -> B -> C -> D -> EX with continuous 1..N order
  const canonicalList: Manga[] = [];
  let globalOrder = 1;
  for (const g of TIER_ORDER) {
    const tierItems = processed
      .filter((m) => m.grade === g)
      .sort((a, b) => (a.order ?? 999999) - (b.order ?? 999999));
    for (const item of tierItems) {
      canonicalList.push({
        ...item,
        order: globalOrder++,
      });
    }
  }

  return canonicalList;
}

// --------------------------------------------------------------------------
// IndexedDB Resilient Storage (No 5MB Quota Limit)
// --------------------------------------------------------------------------
const IDB_TRACKER_NAME = 'MangaTrackerStore_v1';
const IDB_TRACKER_STORE = 'state';

function openTrackerDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      return reject(new Error('IndexedDB not supported'));
    }
    const req = indexedDB.open(IDB_TRACKER_NAME, 1);
    req.onupgradeneeded = (e) => {
      const db = (e.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(IDB_TRACKER_STORE)) {
        db.createObjectStore(IDB_TRACKER_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function saveMangaToIndexedDB(data: Manga[]): Promise<void> {
  try {
    const db = await openTrackerDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction([IDB_TRACKER_STORE], 'readwrite');
      const store = tx.objectStore(IDB_TRACKER_STORE);
      store.put(data, 'manga_list');
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (err) {
    console.error('Error saving manga to IndexedDB:', err);
  }
}

export async function getMangaFromIndexedDB(): Promise<Manga[] | null> {
  try {
    const db = await openTrackerDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction([IDB_TRACKER_STORE], 'readonly');
      const store = tx.objectStore(IDB_TRACKER_STORE);
      const req = store.get('manga_list');
      req.onsuccess = () => {
        const val = req.result;
        if (Array.isArray(val) && val.length > 0) {
          resolve(sanitizeMangaList(val));
        } else {
          resolve(null);
        }
      };
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}

const STORAGE_DELETED_KEY = 'manga_tier_tracker_deleted_ids_v1';

export function getDeletedMangaIds(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_DELETED_KEY);
    if (raw) {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr)) return new Set(arr);
    }
  } catch {}
  return new Set();
}

export function recordDeletedMangaId(id: string): void {
  try {
    const set = getDeletedMangaIds();
    set.add(id);
    localStorage.setItem(STORAGE_DELETED_KEY, JSON.stringify([...set]));
  } catch {}
}

export function unrecordDeletedMangaId(id: string): void {
  try {
    const set = getDeletedMangaIds();
    if (set.has(id)) {
      set.delete(id);
      localStorage.setItem(STORAGE_DELETED_KEY, JSON.stringify([...set]));
    }
  } catch {}
}

function earliestTs(a?: number, b?: number): number | undefined {
  if (a && b) return Math.min(a, b);
  return a || b || undefined;
}

/**
 * Intelligent Merger:
 * - Preserves newly added manga that aren't yet in the remote database
 * - Preserves highest readCh and currentCh
 * - Merges sources safely without losing any
 * - Respects tombstones (deleted manga) so they do not resurrect
 */
export function mergeMangaLists(
  localList: Manga[],
  incomingList: Manga[],
  preferIncoming: boolean = true
): Manga[] {
  if (!incomingList || incomingList.length === 0) return sanitizeMangaList(localList);
  if (!localList || localList.length === 0) return sanitizeMangaList(incomingList);

  const deletedIds = getDeletedMangaIds();
  const localMap = new Map<string, Manga>();
  const localByTitle = new Map<string, Manga>();
  for (const m of localList) {
    if (m.id && !deletedIds.has(m.id)) localMap.set(m.id, m);
    if (m.title && !deletedIds.has(m.id)) localByTitle.set(m.title.trim().toLowerCase(), m);
  }

  const merged: Manga[] = [];
  const handledLocalIds = new Set<string>();

  for (const inc of incomingList) {
    if (deletedIds.has(inc.id)) continue;
    const local = localMap.get(inc.id) || localByTitle.get(inc.title?.trim().toLowerCase());
    if (local) {
      handledLocalIds.add(local.id);

      const readCh = preferIncoming
        ? (inc.readCh !== undefined ? Number(inc.readCh) : (Number(local.readCh) || 0))
        : (local.readCh !== undefined ? Number(local.readCh) : (Number(inc.readCh) || 0));

      // Merge sources without losing user-added links or discovered links (deep-clone to avoid mutating state)
      const baseSources = preferIncoming ? (inc.sources || []) : (local.sources || []);
      const sources = baseSources.map((s) => ({ ...s }));
      const otherSources = preferIncoming ? (local.sources || []) : (inc.sources || []);
      for (const s of otherSources) {
        const existing = sources.find((x) => x.name === s.name || x.url === s.url);
        if (!existing) {
          sources.push({ ...s });
        } else {
          const existingCh = Number(existing.chapter) || 0;
          const otherCh = Number(s.chapter) || 0;
          const existingAt = existing.checkedAt || 0;
          const otherAt = s.checkedAt || 0;
          if (existingAt !== otherAt) {
            // Whichever side read the site's page more recently has the site's real top chapter
            // (it may be lower: a bogus number corrected), so an older copy can't bring the old value back
            if (otherAt > existingAt) {
              existing.chapter = otherCh;
              existing.chapterAt = s.chapterAt;
              existing.checkedAt = s.checkedAt;
            }
          } else {
            existing.chapter = Math.max(existingCh, otherCh);
            // The release time belongs to whichever side has the higher chapter
            existing.chapterAt =
              otherCh > existingCh ? s.chapterAt : otherCh < existingCh ? existing.chapterAt : earliestTs(existing.chapterAt, s.chapterAt);
          }
        }
      }

      const localCh = Number(local.currentCh) || 0;
      const incCh = Number(inc.currentCh) || 0;
      const storedLatestAt =
        incCh > localCh ? inc.latestChapterAt : incCh < localCh ? local.latestChapterAt : earliestTs(local.latestChapterAt, inc.latestChapterAt);

      // Always keep the newer lastUpdatedAt date so today's update badge is never overwritten by an older timestamp
      const lastUpdatedAt =
        local.lastUpdatedAt && inc.lastUpdatedAt
          ? local.lastUpdatedAt > inc.lastUpdatedAt
            ? local.lastUpdatedAt
            : inc.lastUpdatedAt
          : inc.lastUpdatedAt || local.lastUpdatedAt;

      // Pick latestSourceUrl / latestSourceName from the source with the highest chapter
      let bestSource = sources[0];
      let maxSrcCh = 0;
      for (const s of sources) {
        const ch = Number(s.chapter) || 0;
        if (ch > maxSrcCh) {
          maxSrcCh = ch;
          bestSource = s;
        }
      }

      // The sites' chapters decide the newest chapter; both copies' stored numbers only count without any
      const currentCh = maxSrcCh > 0 ? maxSrcCh : Math.max(localCh, incCh);
      // Release time of that chapter: earliest site that has it, else whichever copy had that number
      const latestChapterAt =
        sources
          .filter((s) => (Number(s.chapter) || 0) === currentCh)
          .reduce<number | undefined>((acc, s) => earliestTs(acc, s.chapterAt), undefined) ??
        (currentCh === localCh || currentCh === incCh ? storedLatestAt : undefined);

      if (preferIncoming) {
        // Newer incoming/remote data takes precedence for user edits
        merged.push({
          ...local,
          ...inc,
          readCh,
          currentCh,
          sources,
          grade: inc.grade || local.grade,
          order: inc.order ?? local.order,
          imageUrl: inc.imageUrl || local.imageUrl || '',
          lastUpdatedAt,
          latestChapterAt,
          latestSourceUrl:
            maxSrcCh > 0 && bestSource
              ? bestSource.url
              : inc.latestSourceUrl || local.latestSourceUrl,
          latestSourceName:
            maxSrcCh > 0 && bestSource
              ? bestSource.name
              : inc.latestSourceName || local.latestSourceName,
        });
      } else {
        // Local device takes precedence
        merged.push({
          ...inc,
          ...local,
          readCh,
          currentCh,
          sources,
          grade: local.grade || inc.grade,
          order: local.order ?? inc.order,
          imageUrl: local.imageUrl || inc.imageUrl || '',
          lastUpdatedAt,
          latestChapterAt,
          latestSourceUrl:
            maxSrcCh > 0 && bestSource
              ? bestSource.url
              : local.latestSourceUrl || inc.latestSourceUrl,
          latestSourceName:
            maxSrcCh > 0 && bestSource
              ? bestSource.name
              : local.latestSourceName || inc.latestSourceName,
        });
      }
    } else {
      merged.push(inc);
    }
  }

  // Preserve any local manga that do not exist in the incoming list (newly added manga!)
  for (const local of localList) {
    if (deletedIds.has(local.id)) continue;
    if (!handledLocalIds.has(local.id)) {
      const alreadyIn = merged.some(
        (m) => m.id === local.id || m.title?.trim().toLowerCase() === local.title?.trim().toLowerCase()
      );
      if (!alreadyIn) {
        merged.push(local);
      }
    }
  }

  return sanitizeMangaList(merged);
}

const STORAGE_TIMESTAMP_KEY = 'manga_tier_tracker_timestamp_v1';

export function getLocalMangaTimestamp(): number {
  try {
    const raw = localStorage.getItem(STORAGE_TIMESTAMP_KEY);
    if (raw) {
      const num = Number(raw);
      if (!isNaN(num) && num > 0) return num;
    }
  } catch {}
  return 0;
}

export function setLocalMangaTimestamp(ts: number = Date.now()): void {
  try {
    localStorage.setItem(STORAGE_TIMESTAMP_KEY, String(ts));
  } catch {}
}

export function saveLocalManga(
  data: Manga[],
  explicitTimestamp?: number,
  syncCloud: boolean = true
): void {
  const ts = explicitTimestamp || Date.now();
  setLocalMangaTimestamp(ts);

  // 1. Persist to IndexedDB (virtually unlimited quota)
  saveMangaToIndexedDB(data).catch(() => {});

  // 2. Persist to localStorage
  try {
    localStorage.setItem(STORAGE_MANGA_KEY, JSON.stringify(data));
  } catch (err) {
    console.warn('LocalStorage error, attempting cleanup:', err);
    try {
      localStorage.removeItem('manga_tier_tracker_data_v2');
      localStorage.removeItem('manga_tier_tracker_user_backup_snapshot_v1');
      localStorage.setItem(STORAGE_MANGA_KEY, JSON.stringify(data));
    } catch (e) {
      console.error('LocalStorage write failed (IndexedDB is preserving data):', e);
    }
  }

  // 3. Asynchronously sync to Cloudflare KV backend or local dev server only if syncCloud is true
  if (syncCloud) {
    syncToCloudflare(data, ts).catch(() => {});
  }
}

export function getKnownSites(): KnownSite[] {
  try {
    const raw = localStorage.getItem(STORAGE_SITES_KEY);
    if (raw) {
      const data = JSON.parse(raw);
      if (Array.isArray(data) && data.length > 0) {
        const merged = [...data];
        for (const defSite of DEFAULT_KNOWN_SITES) {
          if (!merged.some((s: any) => s?.url?.includes(defSite.name) || s?.name?.includes(defSite.name))) {
            merged.push(defSite);
          }
        }
        return merged;
      }
    }
  } catch (err) {
    console.error('Error reading sites from localStorage:', err);
  }
  return DEFAULT_KNOWN_SITES;
}

export function saveKnownSites(sites: KnownSite[]): void {
  try {
    localStorage.setItem(STORAGE_SITES_KEY, JSON.stringify(sites));
  } catch (err) {
    console.error('Error saving sites to localStorage:', err);
  }
}

/**
 * Cloudflare Edge Sync with timestamp
 */
export async function syncToCloudflare(mangaList: Manga[], timestamp?: number): Promise<void> {
  try {
    const ts = timestamp || getLocalMangaTimestamp() || Date.now();
    await fetch('/api/manga', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mangaList, updatedAt: ts }),
    });
  } catch {
    // Silently continue if running in local preview mode
  }
}

export function exportBackupJSON(mangaList: Manga[], sites: KnownSite[]): string {
  return JSON.stringify(
    {
      version: 2,
      exportDate: new Date().toISOString(),
      sites,
      mangaList,
    },
    null,
    2
  );
}

export function importBackupJSON(jsonStr: string): { mangaList: Manga[]; sites: KnownSite[] } {
  const data = JSON.parse(jsonStr);
  if (!data || !Array.isArray(data.mangaList)) {
    throw new Error('รูปแบบไฟล์ JSON ไม่ถูกต้อง');
  }
  return {
    mangaList: data.mangaList,
    sites: Array.isArray(data.sites) ? data.sites : DEFAULT_KNOWN_SITES,
  };
}

const STORAGE_BACKUP_KEY = 'manga_tier_tracker_user_backup_snapshot_v1';

export interface UserBackupData {
  date: string;
  count: number;
  mangaList: Manga[];
  sites: KnownSite[];
}

export function saveUserBackup(mangaList: Manga[], sites: KnownSite[]): UserBackupData {
  const backup: UserBackupData = {
    date: new Date().toISOString(),
    count: mangaList.length,
    mangaList,
    sites,
  };
  try {
    localStorage.setItem(STORAGE_BACKUP_KEY, JSON.stringify(backup));
  } catch (err) {
    console.error('Error saving backup to localStorage:', err);
  }

  // Sync to Cloudflare KV backup endpoint if available
  fetch('/api/backup', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(backup),
  }).catch(() => {});

  return backup;
}

export function getUserBackup(): UserBackupData | null {
  try {
    const raw = localStorage.getItem(STORAGE_BACKUP_KEY);
    if (raw) {
      const data = JSON.parse(raw);
      if (data && Array.isArray(data.mangaList)) {
        return data;
      }
    }
  } catch (err) {
    console.error('Error reading backup from localStorage:', err);
  }
  return null;
}

export async function fetchCloudflareBackup(): Promise<UserBackupData | null> {
  try {
    const res = await fetch('/api/backup');
    if (res.ok) {
      const data = await res.json();
      if (data && Array.isArray(data.mangaList)) {
        return data;
      }
    }
  } catch {}
  return null;
}

export function downloadBackupFile(mangaList: Manga[], sites: KnownSite[]): void {
  const jsonStr = exportBackupJSON(mangaList, sites);
  const blob = new Blob([jsonStr], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const dateStr = new Date().toISOString().slice(0, 10);
  a.href = url;
  a.download = `manga-tracker-backup-${dateStr}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

